import { common as c } from '@pendo/aggregations';
import MobileVisitorsAgg from './mobile-visitors.aggregation';
import { connectAggregationToObservable } from '@pendo/agg-connect';
import { of } from 'rxjs';
import { takeWhile, last } from 'rxjs/operators';
import { LOADING_STATES } from '@/constants';
import isArray from 'lodash/isArray';
import { i18n_t } from '@utils/i18n';
import { isPlatformApp } from '@pendo/middleware';
import { AppsService } from '@/common/apps/apps.service';

const { RESOLVED, REJECTED } = LOADING_STATES;

const $t = i18n_t;

export const LEGACY_APP_ID = -323232;

export async function getMobileVisitorCounts (
    appId: c.AppIdType,
    identified: string | boolean = true,
    blacklist: c.BlacklistTypes = 'apply',
) {
    return connectAggregationToObservable(MobileVisitorsAgg, of({ appId, identified, blacklist }), undefined)
        .pipe(
            takeWhile(({ status }) => ![RESOLVED, REJECTED].includes(status), true),
            last(),
        )
        .toPromise();
}

export const PLATFORM_APPS = {
    ALL: '*',
    WEB: 'web',
    EXTENSION: 'extension',
    WEB_LIKE: 'web|extension',
    MOBILE: 'mobile',
};

export const getAppDisplayName = (appId: string | undefined) => {
    if (!appId) return '';

    return {
        [PLATFORM_APPS.ALL]: $t('All Apps'),
        [PLATFORM_APPS.WEB_LIKE]: $t('Web & Extension Apps'),
        [PLATFORM_APPS.WEB]: $t('Web Apps'),
        [PLATFORM_APPS.MOBILE]: $t('Mobile Apps'),
        [PLATFORM_APPS.EXTENSION]: $t('All Extensions'),
    }[appId];
};

// APP_DISPLAY_NAME_MAP IS DEPRECATED
// This is just for backwards compatibility.
// In order to leverage i18n, please replace APP_DISPLAY_NAME_MAP with getAppDisplayName()
export const APP_DISPLAY_NAME_MAP = {
    [PLATFORM_APPS.ALL]: $t('All Apps'),
    [PLATFORM_APPS.WEB_LIKE]: $t('Web & Extension Apps'),
    [PLATFORM_APPS.WEB]: $t('Web Apps'),
    [PLATFORM_APPS.MOBILE]: $t('Mobile Apps'),
    [PLATFORM_APPS.EXTENSION]: $t('All Extensions'),
};

type AppId = string | number;

interface HydratedApp {
    id: AppId;
    displayName?: string;
}

const appsService = AppsService as unknown as {
    getAppDisplayNameFromId: (appId: AppId) => string;
};

export function extractAppIds (apps: readonly HydratedApp[] | null | undefined): AppId[] {
    if (!isArray(apps)) return [];

    return apps.map((app) => app?.id).filter((id): id is AppId => id != null);
}

export function hydrateAppsFromIds (appIds: readonly AppId[] | null | undefined): HydratedApp[] {
    if (!isArray(appIds) || appIds.length === 0) {
        return [];
    }

    return appIds
        .map((id): HydratedApp => ({ id, displayName: appsService.getAppDisplayNameFromId(id) }))
        .filter((app) => Boolean(app.displayName));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function migrateLegacyAppIdToArray (appId: any) {
    if (appId === undefined || appId === null) return [];
    if (isPlatformApp(appId)) return [appId];
    if (isArray(appId)) return appId;

    return [appId];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function transformAggAppIdIntoBareAppId (aggAppId: any) {
    if (!aggAppId) return null;

    if (typeof aggAppId === 'number') return aggAppId;

    const expandAppIdRegex = /expandAppIds\("([^"]+)"\)/;

    const matches = aggAppId.match(expandAppIdRegex);

    if (!matches) return aggAppId;

    return matches[1];
}
